import {
  createContext,
  HTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/shared/lib/utils";
import { useHasFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import {
  TabsNav,
  TabsNavContent,
  TabsNavLink,
  TabsNavLinkProps,
  TabsNavList,
} from "@/shared/components/layout/tabs-nav";
import { NavLink } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { ChevronDown } from "lucide-react";
import { Skeleton } from "@/shared/components/ui/skeleton";

export type DocumentPageProps = HTMLAttributes<HTMLDivElement>;

export function DocumentPage({
  children,
  className,
  ...props
}: DocumentPageProps) {
  return (
    <TabsNav>
      <div className={cn("grid grid-cols-1 gap-2", className)} {...props}>
        {children}
      </div>
    </TabsNav>
  );
}

export type DocumentPageHeaderProps = HTMLAttributes<HTMLDivElement>;

export function DocumentPageHeader({
  children,
  className,
  ...props
}: DocumentPageHeaderProps) {
  return (
    <div className={cn("flex items-center gap-2", className)} {...props}>
      {children}
    </div>
  );
}

export type DocumentPageActionsProps = HTMLAttributes<HTMLDivElement>;

export function DocumentPageActions({
  children,
  className,
  ...props
}: DocumentPageActionsProps) {
  return (
    <div
      className={cn("ml-auto flex items-center space-x-2", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export interface CollapsibleDocumentPageNavigationItem {
  to: string;
  label: ReactNode;
  end?: boolean;
  showError?: boolean;
  errorCount?: number;
}

/**
 * CollapsibleDocumentPageNavigation collapses tabs based on available container width.
 *
 * DocumentPageNavigation only reacts to viewport width. It does not account for sibling
 * header actions that consume horizontal space. This can cause overlap in dense headers.
 * This component measures its own container, keeps priority tabs visible where possible,
 * and moves remaining links into a selector.
 */
export function CollapsibleDocumentPageNavigation({
  links,
  priorityTabs = [],
  maxVisibleTabs = 4,
  label = "Navigation",
  currentLabel,
  mobileBreakpointQuery = "(max-width: 768px)",
}: {
  links: CollapsibleDocumentPageNavigationItem[];
  priorityTabs?: string[];
  maxVisibleTabs?: number;
  label?: string;
  currentLabel?: ReactNode;
  mobileBreakpointQuery?: string;
}) {
  const isMobile = useBreakpoint(mobileBreakpointQuery);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overflowTriggerMeasureRef = useRef<HTMLButtonElement | null>(null);
  const itemMeasureRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemWidths, setItemWidths] = useState<Record<string, number>>({});
  const [overflowTriggerWidth, setOverflowTriggerWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      const nextContainerWidth = containerRef.current?.clientWidth ?? 0;
      const nextOverflowWidth =
        overflowTriggerMeasureRef.current?.offsetWidth ?? 0;
      const nextItemWidths = Object.fromEntries(
        links.map((link) => [
          link.to,
          itemMeasureRefs.current[link.to]?.offsetWidth ?? 0,
        ]),
      );
      setContainerWidth(nextContainerWidth);
      setOverflowTriggerWidth(nextOverflowWidth);
      setItemWidths(nextItemWidths);
    };

    measure();

    const resizeObserver = new ResizeObserver(() => {
      measure();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [links, isMobile]);

  const { visibleLinks, dropdownLinks } = useMemo(() => {
    if (links.length <= 1) {
      return {
        visibleLinks: links,
        dropdownLinks: [] as CollapsibleDocumentPageNavigationItem[],
      };
    }

    if (isMobile) {
      return {
        visibleLinks: [] as CollapsibleDocumentPageNavigationItem[],
        dropdownLinks: links,
      };
    }

    if (priorityTabs.length === 0) {
      return {
        visibleLinks: [] as CollapsibleDocumentPageNavigationItem[],
        dropdownLinks: links,
      };
    }

    const prioritySet = new Set(priorityTabs);
    const priorityLinks = links.filter((link) => prioritySet.has(link.to));
    const optionalLinks = links.filter((link) => !prioritySet.has(link.to));

    const cappedPriority = priorityLinks.slice(0, Math.max(1, maxVisibleTabs));
    const remainingPriority = priorityLinks.slice(cappedPriority.length);

    const gapWidth = 4;
    const widthOf = (item: CollapsibleDocumentPageNavigationItem) =>
      itemWidths[item.to] ?? 0;

    const hiddenByDefault = optionalLinks.length + remainingPriority.length;
    const reserveDropdown =
      hiddenByDefault > 0 ? overflowTriggerWidth + gapWidth : 0;
    const availableWidth = Math.max(0, containerWidth - reserveDropdown);

    const widthWithGaps = (items: CollapsibleDocumentPageNavigationItem[]) =>
      items.reduce(
        (sum, item, idx) => sum + widthOf(item) + (idx > 0 ? gapWidth : 0),
        0,
      );

    if (
      cappedPriority.length === 0 ||
      widthWithGaps(cappedPriority) > availableWidth
    ) {
      return {
        visibleLinks: [] as CollapsibleDocumentPageNavigationItem[],
        dropdownLinks: links,
      };
    }

    const visible = [...cappedPriority];
    const dropdown = [...remainingPriority];

    optionalLinks.forEach((optional) => {
      const canTryAdd = visible.length < maxVisibleTabs;
      if (canTryAdd) {
        const nextVisible = [...visible, optional];
        if (widthWithGaps(nextVisible) <= availableWidth) {
          visible.push(optional);
          return;
        }
      }
      dropdown.push(optional);
    });

    if (dropdown.length === 1) {
      if (widthWithGaps(links) <= containerWidth) {
        return {
          visibleLinks: links,
          dropdownLinks: [] as CollapsibleDocumentPageNavigationItem[],
        };
      }

      const movedLink = visible.pop();
      if (movedLink) {
        dropdown.unshift(movedLink);
      }
    }

    return {
      visibleLinks: visible,
      dropdownLinks: dropdown,
    };
  }, [
    containerWidth,
    isMobile,
    itemWidths,
    links,
    maxVisibleTabs,
    overflowTriggerWidth,
    priorityTabs,
  ]);

  const dropdownHasErrors = dropdownLinks.some(
    (link) => Boolean(link.showError) || (link.errorCount ?? 0) > 0,
  );

  return (
    <div ref={containerRef} className="min-w-0">
      <div className="sr-only absolute -z-10 h-0 overflow-hidden whitespace-nowrap opacity-0">
        {links.map((link) => (
          <span
            key={link.to}
            ref={(node) => {
              itemMeasureRefs.current[link.to] = node;
            }}
            className="inline-flex items-center justify-center rounded-sm px-3 py-1 text-sm font-medium whitespace-nowrap"
          >
            <span>{link.label}</span>
            {typeof link.errorCount === "number" && link.errorCount > 0 ? (
              <span className="ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full border px-1 text-[10px] leading-none">
                {link.errorCount}
              </span>
            ) : null}
          </span>
        ))}
        <Button ref={overflowTriggerMeasureRef} variant="outline" size="sm">
          {currentLabel ?? label}
          <ChevronDown />
        </Button>
      </div>
      <NavigationModeContext.Provider value="tabs">
        <div className="flex min-w-0 items-center gap-1">
          {visibleLinks.length > 0 ? (
            <TabsNavList>
              {visibleLinks.map((link) => (
                <DocumentPageNavigationLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  showError={link.showError}
                  errorCount={link.errorCount}
                >
                  {link.label}
                </DocumentPageNavigationLink>
              ))}
            </TabsNavList>
          ) : null}
          {dropdownLinks.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    dropdownHasErrors &&
                      "border-destructive text-destructive hover:bg-destructive/10",
                  )}
                >
                  <span>{currentLabel ?? label}</span>
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-96" align="start">
                <NavigationModeContext.Provider value="dropdown">
                  <DropdownMenuGroup>
                    {dropdownLinks.map((link) => (
                      <DocumentPageNavigationLink
                        key={link.to}
                        to={link.to}
                        end={link.end}
                        showError={link.showError}
                        errorCount={link.errorCount}
                      >
                        {link.label}
                      </DocumentPageNavigationLink>
                    ))}
                  </DropdownMenuGroup>
                </NavigationModeContext.Provider>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </NavigationModeContext.Provider>
    </div>
  );
}

const NavigationModeContext = createContext<"tabs" | "dropdown">("tabs");

export function DocumentPageNavigation({
  children,
  label,
}: {
  children: ReactNode;
  label?: string;
}) {
  // The breakpoint value may be tuned in the future
  // if experience shows that a different threshold
  // is better.
  const useDropdown = useBreakpoint("(max-width: 1280px)");
  const mode = useDropdown ? "dropdown" : "tabs";

  return (
    <NavigationModeContext.Provider value={mode}>
      {useDropdown ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <span>{label || "Navigation"}</span>
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-96" align="start">
            <DropdownMenuGroup>{children}</DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <TabsNavList>{children}</TabsNavList>
      )}
    </NavigationModeContext.Provider>
  );
}

/**
 * DocumentPageNavigationLink links to a tab of a document page.
 *
 * It is highlighted while the nearest ErrValidationFailedContextProvider holds
 * errors for any of its `fieldName`s, e.g. `["serviceAddress", "contact"]`
 * for a tab showing these two cards. A name matches its key and every key
 * below it ("contact" matches "contact.email"). The fields on the tab reset
 * their errors on input, so the highlight disappears once all are fixed.
 * `hasValidationError` and `showError` highlight it independently of the
 * context.
 */
export function DocumentPageNavigationLink({
  children,
  showError,
  errorCount,
  fieldName,
  hasValidationError,
  ...props
}: TabsNavLinkProps & {
  showError?: boolean;
  errorCount?: number; // display amount of errors for this tab, if > 0
  /** The field names of the fields on the tab. */
  fieldName?: string | string[];
}) {
  const mode = useContext(NavigationModeContext);
  const { hasErrors } = useHasFieldErrors(fieldName);
  const highlight = Boolean(showError || hasValidationError || hasErrors);

  const badge =
    typeof errorCount === "number" && errorCount > 0 ? (
      <span
        className={cn(
          "ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full border px-1 text-[10px] leading-none",
          highlight
            ? "border-destructive text-destructive"
            : "border-muted-foreground/40 text-muted-foreground",
        )}
      >
        {errorCount}
      </span>
    ) : null;

  const content = (
    <span
      className={cn(
        "flex items-center",
        highlight ? "text-destructive" : undefined,
      )}
    >
      <span>{children}</span>
      {badge}
    </span>
  );

  if (mode === "dropdown") {
    return (
      <NavLink to={props.to} end={props.end ?? false}>
        <DropdownMenuItem>{content}</DropdownMenuItem>
      </NavLink>
    );
  }

  return (
    <TabsNavLink {...props} hasValidationError={highlight}>
      {content}
    </TabsNavLink>
  );
}

function useBreakpoint(query: string): boolean {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

export const DocumentPageNavigationOutlet = TabsNavContent;

export type DocumentPageContentProps = HTMLAttributes<HTMLHeadingElement>;

export function DocumentPageContent({
  children,
  className,
  ...props
}: DocumentPageContentProps) {
  return (
    <div className={cn("grid grid-cols-1 gap-2", className)} {...props}>
      {children}
    </div>
  );
}

export function DocumentPageSkeleton() {
  return (
    <div className="grid gap-2">
      <div className="flex">
        <Skeleton className="h-9 w-96 rounded-md" />
        <div className="ml-auto flex gap-2">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-md" />
        </div>
      </div>
      <Skeleton className="h-96 w-full rounded-md" />
    </div>
  );
}
